import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { supabase } from '../../shared/supabase/supabaseClient';
import { showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { clearBootstrapSessionCache } from '../../shell/session/bootstrapMobileSessionService';
import { hydrateSessionCachesFromBootstrap } from '../../shell/session/hydrateSessionCachesFromBootstrap';
import { saveOrganizationBusinessProfileOffline } from '../../shell/connectivity/businessProfileCache';
import { planAllowsCustomer, planAllowsPromotions } from '../../shell/feature-flags/planEntitlementsService';
import { isPublicStoreBusinessTypeAllowed } from '../../shell/feature-flags/publicStoreFeaturesGate';
import { parseOperationMode, type OperationModeMap } from '../../shell/session/domain/operationMode';
import type { BusinessCapability } from '../../shell/session/domain/businessProfile';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import {
  BUSINESS_FUNCTION_DEFINITIONS,
  groupBusinessFunctions,
  isBusinessFunctionToggleable,
  type BusinessFunctionDef,
} from '../domain/businessFunctionsCatalog';

type BreakdownFeature = {
  code: string;
  in_rubro: boolean;
  effective_enabled: boolean;
  override: { capability: string; enabled: boolean; source: string } | null;
};

type BreakdownPayload = {
  features: BreakdownFeature[];
  operation_mode: OperationModeMap;
};

export type BusinessFunctionRow = BusinessFunctionDef & {
  enabled: boolean;
  inRubro: boolean;
  addedByOwner: boolean;
  planLocked: boolean;
  alwaysOn: boolean;
};

export function useBusinessFunctionsScreen() {
  const navigation = useNavigation<any>();
  const profile = useBusinessProfileStore((s) => s.profile);
  const setProfile = useBusinessProfileStore((s) => s.setProfile);
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;

  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [pendingOff, setPendingOff] = useState<BusinessFunctionRow | null>(null);
  const [featureMap, setFeatureMap] = useState<Map<string, BreakdownFeature>>(new Map());
  const [operationMode, setOperationMode] = useState<OperationModeMap>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_my_capability_breakdown');
      if (error) throw error;
      const breakdown = data as BreakdownPayload | null;
      const nextMap = new Map<string, BreakdownFeature>();
      for (const feature of breakdown?.features ?? []) {
        nextMap.set(feature.code, feature);
      }
      setFeatureMap(nextMap);
      setOperationMode(parseOperationMode({ operation_mode: breakdown?.operation_mode ?? {} }));
    } catch (e: unknown) {
      presentUnexpectedFailure({
        module: 'settings',
        op: 'business_functions.load',
        error: e,
        userMessage: 'No se pudieron cargar las funciones.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshProfile = useCallback(async () => {
    const userId = useAuthStore.getState().session?.user?.id;
    if (!userId) return;
    clearBootstrapSessionCache();
    const payload = await hydrateSessionCachesFromBootstrap(userId, { force: true });
    const nextProfile = payload?.business_profile ?? useBusinessProfileStore.getState().profile;
    if (nextProfile) {
      setProfile(nextProfile);
      await saveOrganizationBusinessProfileOffline(nextProfile);
    }
    await load();
  }, [load, setProfile]);

  const rows: BusinessFunctionRow[] = useMemo(() => {
    const defs = BUSINESS_FUNCTION_DEFINITIONS.filter((def) => {
      if (def.code === 'storefront') {
        return isPublicStoreBusinessTypeAllowed(profile?.business_type_code);
      }
      return true;
    });

    return defs.map((def) => {
      const feature = def.code !== 'storefront' ? featureMap.get(def.code) : undefined;
      const inRubro = feature?.in_rubro ?? false;
      const addedByOwner = Boolean(feature?.override?.enabled && !inRubro);
      const alwaysOn = !isBusinessFunctionToggleable(def.code);
      const planLocked =
        def.planLock === 'fiado'
          ? !planAllowsCustomer(entitlements)
          : def.planLock === 'promotions'
            ? !planAllowsPromotions(entitlements)
            : false;

      let enabled: boolean;
      if (def.code === 'storefront') {
        enabled = operationMode.storefront !== false;
      } else if (planLocked) {
        enabled = false;
      } else {
        enabled = feature?.effective_enabled ?? false;
      }

      return {
        ...def,
        enabled,
        inRubro,
        addedByOwner,
        planLocked,
        alwaysOn,
      };
    });
  }, [entitlements, featureMap, operationMode, profile?.business_type_code]);

  const groupedRows = useMemo(() => groupBusinessFunctions(rows), [rows]);

  const persistCapability = useCallback(
    async (code: BusinessCapability, enabled: boolean) => {
      if (!isOwner) return;
      setSavingCode(code);
      try {
        const { error } = await supabase.rpc('toggle_my_organization_capability', {
          p_capability_code: code,
          p_enabled: enabled,
        });
        if (error) throw error;
        await refreshProfile();
        showSuccess('Guardado', enabled ? 'Función activada.' : 'Función desactivada. Los datos quedan.');
      } catch (e: unknown) {
        presentUnexpectedFailure({
          module: 'settings',
          op: 'business_functions.toggle',
          error: e,
          userMessage: 'No se pudo guardar.',
        });
      } finally {
        setSavingCode(null);
      }
    },
    [isOwner, refreshProfile],
  );

  const persistStorefront = useCallback(
    async (enabled: boolean) => {
      if (!profile || !isOwner) return;
      setSavingCode('storefront');
      try {
        const { data, error } = await supabase.rpc('update_my_organization_operation_mode', {
          p_patch: { storefront: enabled },
        });
        if (error) throw error;
        const nextMode =
          data && typeof data === 'object' && !Array.isArray(data)
            ? parseOperationMode({ operation_mode: data as Record<string, unknown> })
            : { ...operationMode, storefront: enabled };
        setOperationMode(nextMode);
        const nextProfile = {
          ...profile,
          business_config: { ...profile.business_config, operation_mode: nextMode },
        };
        setProfile(nextProfile);
        await saveOrganizationBusinessProfileOffline(nextProfile);
        showSuccess('Guardado', enabled ? 'Tienda online activada.' : 'Tienda online desactivada.');
      } catch (e: unknown) {
        presentUnexpectedFailure({
          module: 'settings',
          op: 'business_functions.storefront',
          error: e,
          userMessage: 'No se pudo guardar.',
        });
      } finally {
        setSavingCode(null);
      }
    },
    [isOwner, operationMode, profile, setProfile],
  );

  const persist = useCallback(
    async (row: BusinessFunctionRow, enabled: boolean) => {
      if (row.code === 'storefront') {
        await persistStorefront(enabled);
        return;
      }
      if (row.capability) {
        await persistCapability(row.capability, enabled);
      }
    },
    [persistCapability, persistStorefront],
  );

  const requestToggle = useCallback(
    (row: BusinessFunctionRow, next: boolean) => {
      if (!isOwner || row.alwaysOn || row.planLocked || savingCode) return;
      if (next === row.enabled) return;
      if (!next) {
        setPendingOff(row);
        return;
      }
      void persist(row, true);
    },
    [isOwner, persist, savingCode],
  );

  const confirmHide = useCallback(async () => {
    if (!pendingOff) return;
    const row = pendingOff;
    setPendingOff(null);
    await persist(row, false);
  }, [pendingOff, persist]);

  const openBilling = useCallback(() => navigateInShell(navigation, 'Billing'), [navigation]);

  return {
    isOwner,
    loading,
    groupedRows,
    savingCode,
    pendingOff,
    requestToggle,
    confirmHide,
    closeConfirm: () => setPendingOff(null),
    openBilling,
    reload: load,
  };
}
